/**
 * Package Build Modal
 *
 * Modal for building and downloading a Velociraptor offline collector package.
 * Shows build progress and download button when ready.
 */

import { useEffect, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import type { PackageBuildStatus } from '../../services/collection-packages'
import {
  PackageBuildHeader,
  NoTemplatePanel,
  NoTargetsPanel,
  BuildingSpinner,
  ReadyPanel,
  DownloadSuccessPanel,
  ErrorPanel,
  BuildActions,
} from './package-build-modal.parts'

interface PackageBuildModalProps {
  open: boolean
  onClose: () => void
  status: PackageBuildStatus | null
  building: boolean
  downloading: boolean
  downloadComplete: boolean
  hasTemplate: boolean
  targetCount: number
  error: string | null
  onBuild: () => void
  onBuildCollectorOnly: () => void
  onDownload: () => void
  onStartPolling: () => void
  onStopPolling: () => void
}

interface ModalFlags {
  isReady: boolean
  isFailed: boolean
  isBuilding: boolean
  showNoTargets: boolean
  showError: boolean
}

const READY_STATUSES = new Set(['ready', 'completed'])

function shouldShowNoTargets(
  props: PackageBuildModalProps,
  isReady: boolean,
  isBuilding: boolean
): boolean {
  if (!props.hasTemplate) return false
  if (props.targetCount > 0) return false
  return !isBuilding && !isReady && !props.downloadComplete
}

function deriveFlags(props: PackageBuildModalProps): ModalFlags {
  const statusName = props.status?.status
  const isReady = statusName !== undefined && READY_STATUSES.has(statusName)
  const isFailed = statusName === 'failed'
  const isBuilding = props.building || statusName === 'generating'
  return {
    isReady,
    isFailed,
    isBuilding,
    showNoTargets: shouldShowNoTargets(props, isReady, isBuilding),
    showError: isFailed || Boolean(props.error),
  }
}

interface BuildPollingArgs {
  open: boolean
  status: PackageBuildStatus | null
  onStart: () => void
  onStop: () => void
}

function useBuildPolling({ open, status, onStart, onStop }: BuildPollingArgs): void {
  useEffect(() => {
    if (open && status?.status === 'generating') onStart()
    return () => {
      onStop()
    }
  }, [open, status?.status, onStart, onStop])
}

interface BodyDisplay {
  showNoTemplate: boolean
  showSpinner: boolean
  readyStatus: PackageBuildStatus | null
  showDownloadSuccess: boolean
  showError: boolean
  showNoTargets: boolean
  error: string | null
}

function buildBodyDisplay(props: PackageBuildModalProps, flags: ModalFlags): BodyDisplay {
  const { hasTemplate, downloadComplete, status, error } = props
  return {
    showNoTemplate: !hasTemplate,
    showSpinner: hasTemplate && flags.isBuilding,
    readyStatus: hasTemplate && flags.isReady && !downloadComplete ? status : null,
    showDownloadSuccess: downloadComplete,
    showError: flags.showError,
    showNoTargets: flags.showNoTargets,
    error,
  }
}

function ModalBody({ display }: { display: BodyDisplay }): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <>
      <p className="mb-6 text-sm text-slate-500 dark:text-slate-400">
        {t('collections.packageBuild.description')}
      </p>
      {display.showNoTemplate && <NoTemplatePanel />}
      {display.showNoTargets && <NoTargetsPanel />}
      {display.showSpinner && <BuildingSpinner />}
      {display.readyStatus && <ReadyPanel status={display.readyStatus} />}
      {display.showDownloadSuccess && <DownloadSuccessPanel />}
      {display.showError && <ErrorPanel error={display.error} />}
    </>
  )
}

export function PackageBuildModal(props: PackageBuildModalProps): ReactElement | null {
  const { open, onClose, status, error, onStartPolling, onStopPolling } = props
  useBuildPolling({ open, status, onStart: onStartPolling, onStop: onStopPolling })

  if (!open) return null

  const flags = deriveFlags(props)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-900">
        <PackageBuildHeader onClose={onClose} />
        <ModalBody display={buildBodyDisplay(props, flags)} />
        <BuildActions
          hasTemplate={props.hasTemplate}
          isReady={flags.isReady}
          isFailed={flags.isFailed}
          isBuilding={flags.isBuilding}
          hasError={Boolean(error)}
          building={props.building}
          downloading={props.downloading}
          downloadComplete={props.downloadComplete}
          targetCount={props.targetCount}
          onClose={onClose}
          onBuild={props.onBuild}
          onBuildCollectorOnly={props.onBuildCollectorOnly}
          onDownload={props.onDownload}
        />
      </div>
    </div>
  )
}
